import type { ReactElement, ReactNode } from 'react';
import { Info } from 'lucide-react';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import {
  hasSelfDeclaredMismatch,
  installedVersions,
  resolutionMethodLabel,
  selfDeclaredVersions,
} from './mcpVersionState';

interface MCPVersionDetailsPopoverProps {
  row: MCPServerListItem;
}

function Row({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-2 items-baseline">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-xs break-words min-w-0">{children}</dd>
    </div>
  );
}

/**
 * Details behind the Version column (KIR-4844): how the version was obtained,
 * when, from which spec — and the value the server reports about itself,
 * labelled as the claim it is.
 *
 * Copy is written for a reader who does not know what npx, a repo digest or a
 * floating spec are (KIR-5042). The distinctions are kept; the vocabulary is
 * not.
 *
 * A popover, not a drawer: the table has no detail drawer, and its one
 * expansion slot is taken by the gateway tool list and wanted by KIR-4644.
 */
export function MCPVersionDetailsPopover({
  row,
}: MCPVersionDetailsPopoverProps): ReactElement {
  const installed = installedVersions(row.versions);
  const methods = row.resolutionMethods ?? [];
  const ecosystems = row.ecosystems ?? [];
  const handshake = selfDeclaredVersions(row.versions).map(e => e.version);
  const mismatch = hasSelfDeclaredMismatch(row.versions);
  // Headline entry, so the panel explains what the cell actually shows.
  const headline = installed[0];
  // Truthy, not `!= null`: a blank spec reaches here and is the absent case.
  const specs = installed
    .map(e => e.declaredSpec)
    .filter((v): v is string => Boolean(v));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Gateway rows are click-to-expand (KIR-3940); don't toggle them.
          onClick={event => event.stopPropagation()}
          aria-label="Version details"
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 space-y-3"
        onClick={event => event.stopPropagation()}
      >
        <dl className="space-y-1.5">
          <Row label="Version found">
            {installed.length > 0 ? (
              installed.map(e => e.version).join(', ')
            ) : (
              <span className="text-muted-foreground">
                None found on any machine
              </span>
            )}
          </Row>

          {methods.length > 0 && (
            <Row label="How we know">
              {methods.map(resolutionMethodLabel).join(', ')}
            </Row>
          )}

          {ecosystems.length > 0 && (
            <Row label="Ecosystem">{ecosystems.join(', ')}</Row>
          )}

          {headline?.resolvedAt && (
            <Row label="Last checked">
              <RelativeTimeWithTooltip date={headline.resolvedAt} inline />
            </Row>
          )}

          {specs.length > 0 && (
            <Row label="Configured as">{specs.join(', ')}</Row>
          )}

          {/* Omitted when null: "undetermined" is not "pinned". */}
          {headline?.floating != null && (
            <Row label="Updates">
              {headline.floating
                ? 'Changes on every start'
                : 'Pinned to one version'}
            </Row>
          )}
        </dl>

        <div className="border-t pt-2 space-y-1">
          <div className="text-xs font-medium">Reported by the server</div>
          <div className="text-xs text-muted-foreground">
            {handshake.length > 0 ? handshake.join(', ') : 'Nothing reported'}
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            {mismatch
              ? 'Different from what was found on the machine. That is normal: several well-known servers have a version written into them that nobody updated.'
              : 'What the server says its version is. Nobody checked it against the installed copy.'}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
